import { createDate } from './utils/dateUtils';

export function isProduction() {
  return process.env.NODE_ENV === 'production';
}

const innerEnvs = ['mercury', 'venus', 'mars', 'localhost'];
const experimentalEnvs = ['mercury', 'localhost'];

export function isExperimentalEnvironment() {
  const host = window.location.host;
  return experimentalEnvs.some((name) => host.includes(name));
}

export function isInnerEnvironment() {
  const host = window.location.host;
  return innerEnvs.some((name) => host.includes(name));
}

export const API_VERSION = '/api/v1';

export const START_DATE_FOR_REQUESTS = createDate({ days: -3650 });

/**
 * How far back the patient page loads EXECUTION RESULTS by default.
 *
 * `/movement/results` used to be fetched with START_DATE_FOR_REQUESTS — ten years,
 * and no end date — on every patient open, so opening one patient downloaded their
 * entire history to draw a screen that shows one day.
 *
 * 180 days deliberately matches the storage hot tier: session video tiers to
 * Glacier IR at 180 days (docs/security/retention-and-lifecycle-plan.md), and the
 * Sessions list is built FROM these rows, so capping here means the portal never
 * offers a session whose video would incur a Glacier retrieval unless the
 * clinician explicitly asks for older history. Keep the two numbers in step.
 *
 * Older history is still reachable — see `loadEarlierResultsAction` and the
 * "Load earlier" control. That pairing is not optional: without it this cap would
 * make old sessions UNREACHABLE rather than on-demand, which is worse than the
 * slow load it replaces.
 */
export const RESULTS_WINDOW_DAYS = 180;

export const resultsWindowStart = () => createDate({ days: -RESULTS_WINDOW_DAYS });

/**
 * Start date for an arbitrary window. A function, not a constant, for the same
 * reason as resultsWindowStart: a module-level \`createDate\` is frozen at import
 * time, so a long-lived tab would keep asking from the date it was opened.
 */
export const resultsWindowStartFor = (days: number) => createDate({ days: -days });

/**
 * The ranges the "Load earlier" control offers, widest last.
 *
 * Deliberately a short list of steps rather than one all-or-nothing jump to the
 * full history: a clinician who wants last year should not have to download a
 * decade to get it, and that request is the very thing this whole window exists
 * to avoid. The control only ever offers steps WIDER than what is already
 * loaded, and results merge rather than replace, so widening is additive and a
 * clinician can keep stepping out without re-fetching what they already have.
 *
 * The last entry must stay equal to START_DATE_FOR_REQUESTS' 3650 days — it is
 * what "everything" means here, and `loadedResultsWindowDays` reaching it is
 * what retires the control.
 */
export const RESULTS_WINDOW_OPTIONS = [
  { label: 'Last year', days: 365 },
  { label: 'Last 2 years', days: 730 },
  { label: 'Last 5 years', days: 1825 },
  { label: 'Last 10 years', days: 3650 },
] as const;

export const MAX_RESULTS_WINDOW_DAYS = Math.max(
  ...RESULTS_WINDOW_OPTIONS.map(({ days }) => days),
);

export const END_DATE_FOR_REQUESTS = createDate({ days: 3650 });
